"use client";

import React, { useCallback, useState } from "react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { GoogleDriveBrowser } from "@/components/crm/drive/GoogleDriveBrowser";
import type { MarketingBrand } from "@/lib/marketing/brands";
import { useMarketingBrands } from "@/lib/hooks/marketing/useMarketingBrands";
import type { GoogleDriveFile } from "@/lib/hooks/google-drive/use-google-drive-browser";
import {
  useMarketingAssetPicker,
  type MaterializedMarketingAsset,
} from "@/lib/hooks/marketing/useMarketingAssetPicker";

export interface MarketingAssetLibraryModalProps {
  /**
   * Brand whose Drive asset folder to browse. REQUIRED — a post/asset belongs
   * to exactly one brand, chosen up front. Pass `null` while the caller hasn't
   * chosen a brand yet; the modal renders a "pick a brand first" state.
   */
  brand: MarketingBrand | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after the picked Drive file has been materialized into the
   * social-media-assets bucket. Receives the stable public URL + provenance.
   */
  onAssetSelected: (asset: MaterializedMarketingAsset) => void;
}

/**
 * Marketing Asset Library — REUSES the existing `GoogleDriveBrowser` (built-in
 * debounced name search, drill-into-subfolders, thumbnails) scoped to a brand's
 * Drive root folder. On file select the file is materialized into the public
 * `social-media-assets` bucket (see useMarketingAssetPicker) and the resulting
 * `{ publicUrl, driveFileId, driveFileName, mediaKind }` is returned to the
 * caller — ready to attach to a social post or embed in an email template.
 *
 * Search: the browser's built-in name filter (`name contains`) is used. Drive
 * full-text CONTENT search (`fullText contains`) is also supported by the same
 * Drive browse API (it passes `q` straight through) and could back a future
 * "search inside documents" enhancement — not wired here because marketing
 * assets are images/videos matched by filename.
 */
export function MarketingAssetLibraryModal({
  brand,
  open,
  onOpenChange,
  onAssetSelected,
}: MarketingAssetLibraryModalProps) {
  const { materializeFile } = useMarketingAssetPicker();
  const [attachingId, setAttachingId] = useState<string | null>(null);

  // Brand identity (display name + Drive root) comes from the registry over the
  // API — it used to be a hardcoded literal keyed by a compile-time union.
  const { byId: brandById } = useMarketingBrands();
  const brandConfig = brand ? brandById(brand) : null;

  const handleFileSelect = useCallback(
    async (file: GoogleDriveFile) => {
      if (!brand) return;
      // Only images/videos can back a post/email; folders are drill-in, not select.
      if (file.isFolder) return;
      const isMedia =
        file.mimeType.startsWith("image/") || file.mimeType.startsWith("video/");
      if (!isMedia) {
        toast.error("Pick an image or video — that file type can't be attached.");
        return;
      }

      setAttachingId(file.id);
      try {
        const asset = await materializeFile(brand, file);
        onAssetSelected(asset);
        toast.success(`Attached: ${asset.driveFileName}`);
        onOpenChange(false);
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Could not attach that file.",
        );
      } finally {
        setAttachingId(null);
      }
    },
    [brand, materializeFile, onAssetSelected, onOpenChange],
  );

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Marketing Asset Library"
      description={
        brandConfig
          ? `Browse ${brandConfig.displayName}'s Drive assets and pick an image or video.`
          : "Choose a brand to browse its marketing assets."
      }
      maxWidth="4xl"
    >
      {!brand || !brandConfig ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          Select a brand first — each post or asset belongs to one brand.
        </div>
      ) : (
        <div className="space-y-4">
          {attachingId && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Copying to the asset library…
            </div>
          )}
          <GoogleDriveBrowser
            key={brand}
            initialParentId={brandConfig.driveRootFolderId ?? undefined}
            initialParentName={`${brandConfig.displayName} Assets`}
            onFileSelect={handleFileSelect}
            allowDownload={false}
            allowView={false}
            allowCreateFolder={false}
            allowUpload={false}
            allowDelete={false}
            compactMode
            maxHeight="420px"
            className="border-0 shadow-none"
          />
        </div>
      )}
    </ResponsiveModal>
  );
}
